/**
 * Shapes and pure helpers for the admin-only "System knowledge" view (see
 * AgentSystemKnowledge.tsx): system_lessons mined from tool calls (or seeded
 * from the docs), which take effect for every organisation only after a
 * msys-ai-admin member approves them. Mirrors what the GraphQL query
 * `agentSystemLessons` returns for them.
 *
 * These helpers must stay i18n-free: they return enums, numbers and other
 * data the component turns into translated strings, never rendered text
 * themselves.
 */

/** Sanitised trace of one occurrence a lesson was mined from: no tool
 * input/result and no conversation/message/organisation ids. */
export type SystemLessonEvidence = {
  at: string; // ISO
  tools: string[];
  error: string | null; // normalised failure text, null when nothing failed
  recovered: boolean; // whether a retry after the failure then succeeded
  note: string;
};

export type SystemLessonKind = "tool_usage" | "system_concept";
export type SystemLessonStatus =
  | "proposed"
  | "approved"
  | "rejected"
  | "retired";
/** The statuses an admin can decide; `proposed` is only a lesson's initial status. */
export type SystemLessonDecisionStatus = Exclude<
  SystemLessonStatus,
  "proposed"
>;
export type SystemLessonSource = "mined" | "docs";

/** One status change of a system lesson: who, when, from → to and why. */
export type SystemLessonDecision = {
  at: string; // ISO
  from: SystemLessonStatus;
  to: SystemLessonStatus;
  byUserId: string | null; // null for the automatic mining pass
  byName: string;
  reason: string; // may be "" where the action allows an empty reason
};

export type SystemLesson = {
  id: string;
  kind: SystemLessonKind;
  lesson: string;
  tools: string[];
  status: SystemLessonStatus;
  source: SystemLessonSource;
  evidence: SystemLessonEvidence[];
  occurrences: number;
  organisationCount: number;
  occurrencesSinceApproval: number;
  proposedAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisions: SystemLessonDecision[];
  decidedByName: string | null;
};

export type SystemLessonRunReport = {
  scannedMessages: number;
  signals: number;
  proposed: number;
  updated: number;
  errors: string[];
};

export type SystemLessonRun = {
  id: string;
  trigger: "schedule" | "manual";
  startedAt: string;
  finishedAt: string | null;
  report: SystemLessonRunReport | null;
};

/** The panel's filter/tabs; "rejected" also covers "retired" (both are past decisions). */
export type SystemLessonTab = "proposed" | "approved" | "rejected";

const TAB_STATUSES: Record<SystemLessonTab, readonly SystemLessonStatus[]> = {
  proposed: ["proposed"],
  approved: ["approved"],
  rejected: ["rejected", "retired"],
};

/**
 * The four decision buttons on a card. "approved" is reachable from two
 * different actions with different wording ("Approve" the first time,
 * "Approve again" once it was rejected/retired before), so this is kept
 * distinct from {@link SystemLessonStatus}.
 */
export type SystemLessonDecisionAction =
  | "approve"
  | "reapprove"
  | "reject"
  | "retire";

export const DECISION_ACTION_TARGET_STATUS: Record<
  SystemLessonDecisionAction,
  SystemLessonDecisionStatus
> = {
  approve: "approved",
  reapprove: "approved",
  reject: "rejected",
  retire: "retired",
};

/** Reject and retire require a non-blank reason; approve/reapprove don't. */
export function isReasonRequiredForAction(
  action: SystemLessonDecisionAction
): boolean {
  return action === "reject" || action === "retire";
}

/** Drives the confirm button's disabled state in the decision dialog. */
export function canConfirmDecision(
  action: SystemLessonDecisionAction,
  reason: string
): boolean {
  return !isReasonRequiredForAction(action) || reason.trim() !== "";
}

/** Newest first, by `at` — independent of the array's stored order. */
export function sortDecisionsNewestFirst(
  decisions: readonly SystemLessonDecision[]
): SystemLessonDecision[] {
  return [...decisions].sort((a, b) =>
    a.at < b.at ? 1 : a.at > b.at ? -1 : 0
  );
}

/** `null` while the lesson has never been decided on yet. */
export function latestDecision(
  lesson: SystemLesson
): SystemLessonDecision | null {
  return sortDecisionsNewestFirst(lesson.decisions)[0] ?? null;
}

/** A decision made by the automatic mining pass rather than an admin. */
export function isAutomaticDecision(decision: SystemLessonDecision): boolean {
  return decision.byUserId === null;
}

export function lessonsForTab(
  lessons: readonly SystemLesson[],
  tab: SystemLessonTab
): SystemLesson[] {
  const statuses = TAB_STATUSES[tab];
  return lessons.filter(lesson => statuses.includes(lesson.status));
}

export function tabCounts(
  lessons: readonly SystemLesson[]
): Record<SystemLessonTab, number> {
  return {
    proposed: lessonsForTab(lessons, "proposed").length,
    approved: lessonsForTab(lessons, "approved").length,
    rejected: lessonsForTab(lessons, "rejected").length,
  };
}

/** Drives the header button's dot badge. */
export function hasOpenProposals(lessons: readonly SystemLesson[]): boolean {
  return lessons.some(lesson => lesson.status === "proposed");
}

/** Wording may be changed while the lesson has not been retired/rejected yet. */
export function isLessonEditable(status: SystemLessonStatus): boolean {
  return status === "proposed" || status === "approved";
}

/** Only meaningful once approved; `null` otherwise so callers can skip it. */
export function occurrencesSinceApproval(lesson: SystemLesson): number | null {
  if (lesson.status !== "approved") return null;
  return lesson.occurrencesSinceApproval;
}

/**
 * The pieces for the "last analysis" line; `null` while there has never been
 * a finished run yet (a run in progress has no `finishedAt` either, so it
 * renders nothing here — the "Analyse now" button and its own busy state
 * cover that).
 */
export function runSummaryParts(run: SystemLessonRun | null): {
  finishedAt: string;
  scannedMessages: number;
  newProposals: number;
  errors: string[];
} | null {
  if (!run || !run.finishedAt || !run.report) return null;
  return {
    finishedAt: run.finishedAt,
    scannedMessages: run.report.scannedMessages,
    newProposals: run.report.proposed,
    errors: run.report.errors,
  };
}
